import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { CheckCircle, AlertCircle, Clock, Send } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AvaliacaoExterna = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [avaliacao, setAvaliacao] = useState(null);
  const [candidato, setCandidato] = useState(null);
  const [jaConcluida, setJaConcluida] = useState(false);
  const [erro, setErro] = useState(null);
  const [respostas, setRespostas] = useState({});
  const [paginaAtual, setPaginaAtual] = useState(0);

  useEffect(() => {
    carregarAvaliacao();
  }, [token]);

  const carregarAvaliacao = async () => {
    try {
      setLoading(true);
      console.log('🔍 Tentando carregar avaliação externa com token:', token);
      console.log('🔍 API URL:', API);
      console.log('🔍 URL completa:', `${API}/api/fit-cultural/avaliacao-externa/${token}`);
      
      const response = await axios.get(`${API}/api/fit-cultural/avaliacao-externa/${token}`);
      
      console.log('✅ Resposta completa recebida:', JSON.stringify(response.data, null, 2));
      console.log('✅ Avaliação:', response.data.avaliacao);
      console.log('✅ Perguntas:', response.data.avaliacao?.perguntas);
      console.log('✅ Primeira pergunta:', response.data.avaliacao?.perguntas?.[0]);
      
      if (response.data.ja_concluida) {
        setJaConcluida(true);
      } else {
        setAvaliacao(response.data.avaliacao);
        setCandidato(response.data.candidato);
        console.log('✅ Estado atualizado - Avaliação:', response.data.avaliacao);
      }
    } catch (error) {
      console.error('❌ Erro completo ao carregar avaliação:', error);
      console.error('❌ Detalhes do erro:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });
      
      if (error.response?.status === 404) {
        setErro('Avaliação não encontrada ou link inválido.');
      } else if (error.response?.status === 410) {
        setErro('Este link expirou. Por favor, entre em contato com a empresa.');
      } else {
        setErro(`Erro ao carregar avaliação: ${error.response?.data?.detail || error.message || 'Erro desconhecido'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResposta = (perguntaId, valor) => {
    setRespostas(prev => ({
      ...prev,
      [perguntaId]: valor
    }));
  };

  const handleProximaPergunta = () => {
    const perguntaAtual = avaliacao.perguntas[paginaAtual];
    if (!respostas[perguntaAtual.id]) {
      toast.error('Por favor, responda a pergunta antes de continuar');
      return;
    }
    setPaginaAtual(prev => prev + 1);
  };

  const handlePerguntaAnterior = () => {
    setPaginaAtual(prev => prev - 1);
  };

  const handleSubmit = async () => {
    // Verificar se todas as perguntas foram respondidas
    const perguntasNaoRespondidas = avaliacao.perguntas.filter(p => !respostas[p.id]);
    if (perguntasNaoRespondidas.length > 0) {
      toast.error('Por favor, responda todas as perguntas antes de enviar');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/api/fit-cultural/avaliacao-externa/${token}/responder`, {
        respostas: respostas
      });
      
      toast.success('Avaliação concluída com sucesso!');
      setJaConcluida(true);
    } catch (error) {
      console.error('Erro ao enviar respostas:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar respostas');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <Clock className="h-12 w-12 text-blue-600 mx-auto mb-4 animate-spin" />
            <p className="text-lg">Carregando avaliação...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-red-200">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-16 w-16 text-red-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-red-900 mb-2">Ops!</h2>
            <p className="text-red-700 mb-4">{erro}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (jaConcluida) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-green-200">
          <CardContent className="p-8 text-center">
            <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-green-900 mb-2">Avaliação Concluída!</h2>
            <p className="text-green-700 mb-4">
              Obrigado por completar a avaliação. Suas respostas foram registradas com sucesso.
            </p>
            <p className="text-sm text-green-600">
              A empresa entrará em contato em breve.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const perguntaAtual = avaliacao.perguntas[paginaAtual];
  const progresso = ((paginaAtual + 1) / avaliacao.perguntas.length) * 100;
  const isUltimaPergunta = paginaAtual === avaliacao.perguntas.length - 1;

  console.log('📊 Estado atual:', {
    avaliacao,
    perguntaAtual,
    paginaAtual,
    totalPerguntas: avaliacao.perguntas?.length,
    tipoPergunta: perguntaAtual?.tipo
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <Card className="mb-6 border-none shadow-lg bg-gradient-to-r from-blue-600 to-indigo-600">
          <CardContent className="p-6 text-white">
            <h1 className="text-3xl font-bold mb-2">{avaliacao.nome}</h1>
            <p className="text-blue-100 mb-4">{avaliacao.descricao}</p>
            <div className="bg-white/20 rounded-lg p-3">
              <p className="text-sm"><strong>Candidato:</strong> {candidato.nome}</p>
              {candidato.cargo_vaga && (
                <p className="text-sm"><strong>Vaga:</strong> {candidato.cargo_vaga}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Barra de Progresso */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Pergunta {paginaAtual + 1} de {avaliacao.perguntas.length}</span>
            <span>{Math.round(progresso)}% completo</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-blue-600 to-indigo-600 h-3 rounded-full transition-all duration-300"
              style={{ width: `${progresso}%` }}
            />
          </div>
        </div>

        {/* Pergunta Atual */}
        <Card className="mb-6 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
            <CardTitle className="text-xl">{perguntaAtual.texto}</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {(perguntaAtual.tipo === 'likert_5' || perguntaAtual.tipo === 'likert' || !perguntaAtual.tipo) && (
              <div className="space-y-3">
                <p className="text-sm text-gray-600 mb-4">
                  Selecione uma opção de 1 (Discordo Totalmente) a 5 (Concordo Totalmente)
                </p>
                {[1, 2, 3, 4, 5].map(valor => (
                  <label 
                    key={valor}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      respostas[perguntaAtual.id] === valor
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name={`pergunta-${perguntaAtual.id}`}
                      value={valor}
                      checked={respostas[perguntaAtual.id] === valor}
                      onChange={() => handleResposta(perguntaAtual.id, valor)}
                      className="mr-3"
                    />
                    <span className="font-semibold mr-2">{valor}</span>
                    <span className="text-gray-600">
                      {valor === 1 && '- Discordo Totalmente'}
                      {valor === 2 && '- Discordo Parcialmente'}
                      {valor === 3 && '- Neutro'}
                      {valor === 4 && '- Concordo Parcialmente'}
                      {valor === 5 && '- Concordo Totalmente'}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Botões de Navegação */}
        <div className="flex justify-between gap-4">
          <Button
            variant="outline"
            onClick={handlePerguntaAnterior}
            disabled={paginaAtual === 0}
            className="flex-1"
          >
            ← Anterior
          </Button>
          
          {isUltimaPergunta ? (
            <Button
              onClick={handleSubmit}
              disabled={submitting || !respostas[perguntaAtual.id]}
              className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
            >
              <Send className="h-4 w-4 mr-2" />
              {submitting ? 'Enviando...' : 'Enviar Avaliação'}
            </Button>
          ) : (
            <Button
              onClick={handleProximaPergunta}
              disabled={!respostas[perguntaAtual.id]}
              className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
            >
              Próxima →
            </Button>
          )}
        </div>

        {/* Resumo de Respostas */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg">Resumo das Respostas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-10 gap-2">
              {avaliacao.perguntas.map((p, idx) => (
                <div
                  key={p.id}
                  className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                    respostas[p.id]
                      ? 'bg-green-500 text-white'
                      : idx === paginaAtual
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {idx + 1}
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-600 mt-3">
              {Object.keys(respostas).length} de {avaliacao.perguntas.length} perguntas respondidas
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AvaliacaoExterna;
